import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDateIn, formatTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Keyword = Schema<'OutreachKeywordOut'>
type Pack = Schema<'KeywordPackOut'>
type Filter = Schema<'DiscoveryFilterOut'>
type LastRun = Schema<'DiscoveryLastRunOut'>
type Suggestion = Schema<'PackSuggestionOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * The three windows the screen offers, mirroring `design/screens/outreach.html#packs`.
 *
 * The API takes any value from 1 to 720 hours (`ck_discovery_settings_age_within_bounds`)
 * and this list is the screen's opinion rather than the server's limit — a tenant
 * whose stored window is none of the three still sees it, via `windowOptions`
 * below, instead of silently having it rewritten to 24 hours by a `<select>` that
 * could not represent it.
 */
const WINDOWS: { hours: number; label: string }[] = [
  { hours: 24, label: '24 hours — freshest, fewest results' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
]

function windowOptions(current: number): { hours: number; label: string }[] {
  if (WINDOWS.some((option) => option.hours === current)) return WINDOWS
  return [...WINDOWS, { hours: current, label: `${String(current)} hours` }].sort(
    (a, b) => a.hours - b.hours,
  )
}

/**
 * "What we search for" — `design/screens/outreach.html#packs`, audit row §B5,
 * the last open row in `docs/MOCKUP-GAP-AUDIT.md`.
 *
 * Four things behind one screen, and the API answers all four in one `GET`: the
 * organisation's own keywords, the curated packs it can start from, the post
 * filter that decides what counts as a lead, and what the last run did with them.
 *
 * THIS IS A POST SEARCH, AND THE SCREEN SAYS SO
 * -----------------------------------------------
 * `docs/DESIGN-AUDIT.md` §4.1 records the most expensive defect the design audit
 * found: this screen was *"Who we're looking for"* with job titles, a Locations
 * field and a Company-size dropdown — which is LinkedIn *people* search. §5 and
 * the ported `../linkmailer/scraper.py` both search *posts*: each keyword is run
 * against post results sorted by latest, recent ones are kept, and the lead is
 * whoever wrote one. None of those three controls exists in post search, so none
 * of them is here and none of them is coming back. The controls that are here are
 * the ones post search actually has — keywords, an age window, and phrases that
 * disqualify a post.
 *
 * WHERE THE LAST-RUN PANEL DEVIATES FROM THE PROTOTYPE, AND WHY
 * ---------------------------------------------------------------
 * The prototype draws five rows and this draws six. The extra one is **No address
 * in the post**, which `app/outreach/posts.read_page` calls the commonest drop of
 * all — and without it the rows do not sum to *Posts read*, which is the single
 * fastest way to make somebody stop believing a funnel. *Found under another
 * keyword* is here for the same arithmetic: it is neither a filter nor a fault,
 * just the same post surfacing twice.
 *
 * The heading is *Latest run* with a date rather than the prototype's
 * *Last run — 04:30 IST*: `discovery_pages` carries no run identifier, so
 * `app/db/packs.LastRun` defines a run as one IST day's filed pages and the
 * heading says which day rather than implying a precision the table cannot back.
 *
 * WHY THE PACK CARDS CARRY NO "~180 POSTS A DAY"
 * ------------------------------------------------
 * The prototype's cards read *"34 keywords · 61 exclusions · ~180 posts a day"*.
 * The first two are real and rendered. The third is a forecast of what LinkedIn
 * will return for terms this tenant has never run, and there is nothing in
 * AugRank that could compute it — it would be an invented number on the screen a
 * tenant uses to decide where their scrape budget goes. This is the same call
 * §B3 made about the prototype's *"₹8.4L in play"*.
 */
export function NichePacks(): JSX.Element {
  const { role } = useActiveTenant()
  const manageable = may(role, 'discovery:manage')
  const screen = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/keywords', { signal })),
    [],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">What we search for</h1>
        <span className="app-spacer" />
      </div>

      {screen.state.status === 'loading' ? (
        <Loading label="Loading your keywords" lines={6} />
      ) : null}
      {screen.state.status === 'error' ? (
        <ErrorState
          failure={screen.state.failure}
          onRetry={screen.refetch}
          title="Could not load your keywords"
        />
      ) : null}
      {screen.state.status === 'ready' ? (
        <div className="ar-grid ar-grid--2">
          <div className="ar-stack">
            <KeywordsCard
              keywords={screen.state.data.keywords}
              suggestions={screen.state.data.suggestions}
              replyKeywords={screen.state.data.reply_keywords}
              manageable={manageable}
              onChanged={screen.refetch}
            />
            <FilterCard
              filter={screen.state.data.settings}
              manageable={manageable}
              onChanged={screen.refetch}
            />
          </div>
          <div className="ar-stack">
            <PacksCard
              packs={screen.state.data.packs}
              manageable={manageable}
              onChanged={screen.refetch}
            />
            <LastRunCard run={screen.state.data.last_run} />
          </div>
        </div>
      ) : null}
    </div>
  )
}

function KeywordsCard({
  keywords,
  suggestions,
  replyKeywords,
  manageable,
  onChanged,
}: {
  keywords: Keyword[]
  suggestions: Suggestion[]
  replyKeywords: string[]
  manageable: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const [draft, setDraft] = useState('')
  const toggle = useMutation((value: { id: string; enabled: boolean }) =>
    call(() =>
      api.PATCH('/api/v1/outreach/keywords/{keyword_id}', {
        params: { path: { keyword_id: value.id } },
        body: { enabled: value.enabled },
      }),
    ),
  )
  const add = useMutation((value: { keyword: string; source: 'custom' | 'suggested' }) =>
    call(() => api.POST('/api/v1/outreach/keywords', { body: value })),
  )

  async function onToggle(keyword: Keyword): Promise<void> {
    const result = await toggle.run({ id: keyword.id, enabled: !keyword.enabled })
    if (result.ok) onChanged()
  }

  async function onAdd(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const keyword = draft.trim()
    if (!keyword) return
    const result = await add.run({ keyword, source: 'custom' })
    if (result.ok) {
      setDraft('')
      toast.show({ kind: 'success', title: `Added “${keyword}”.` })
      onChanged()
    }
  }

  async function onAccept(suggestion: Suggestion): Promise<void> {
    const result = await add.run({ keyword: suggestion.keyword, source: 'suggested' })
    if (result.ok) {
      toast.show({ kind: 'success', title: `Added “${suggestion.keyword}”.` })
      onChanged()
    }
  }

  const enabled = keywords.filter((keyword) => keyword.enabled).length

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Your keywords</h3>
        <span className="ar-card__spacer" />
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          {enabled} of {keywords.length} searched
        </span>
      </div>
      <div className="ar-card__body ar-stack">
        {keywords.length === 0 ? (
          <Empty
            title="No keywords yet"
            body="Add a phrase a buyer would post, or start from one of the curated packs."
          />
        ) : (
          <div className="app-chips">
            {keywords.map((keyword) => (
              <button
                key={keyword.id}
                type="button"
                className="app-chip"
                aria-pressed={keyword.enabled}
                disabled={!manageable || toggle.pending}
                onClick={() => void onToggle(keyword)}
                title={
                  keyword.pack_slug
                    ? `From the ${keyword.pack_slug} pack, version ${String(keyword.pack_version ?? 1)}`
                    : 'Added by your organisation'
                }
              >
                {keyword.keyword}
              </button>
            ))}
          </div>
        )}
        <InlineError failure={toggle.state.failure} />

        <div>
          <div className="ar-muted" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            Suggested for you
          </div>
          {suggestions.length === 0 ? (
            <span className="ar-field__help">
              {replyKeywords.length === 0
                ? 'Suggestions are drawn from the posts that produced your replies. You have not had any yet.'
                : 'Nothing new to suggest — you already have every term from the packs your replies came from.'}
            </span>
          ) : (
            <>
              <div className="app-chips">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.keyword}
                    type="button"
                    className="app-chip"
                    aria-pressed={false}
                    disabled={!manageable || add.pending}
                    onClick={() => void onAccept(suggestion)}
                  >
                    + {suggestion.keyword}
                  </button>
                ))}
              </div>
              <span className="ar-field__help">
                Drawn from the posts that produced your last 20 replies — the rest of the{' '}
                {suggestions[0]?.pack_name ?? 'matching'} pack.
              </span>
            </>
          )}
        </div>

        {manageable ? (
          <form className="ar-row" onSubmit={(event) => void onAdd(event)}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Add a keyword"
                value={draft}
                maxLength={200}
                placeholder="looking for interior contractor"
                help="A phrase a buyer would write in a post — not a job title."
                onChange={(event) => {
                  setDraft(event.target.value)
                  add.reset()
                }}
              />
            </div>
            <Button type="submit" tone="secondary" busy={add.pending} disabled={!draft.trim()}>
              Add keyword
            </Button>
          </form>
        ) : null}
        {add.state.failure ? <InlineError failure={add.state.failure} /> : null}
      </div>
    </div>
  )
}

function FilterCard({
  filter,
  manageable,
  onChanged,
}: {
  filter: Filter
  manageable: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const [hours, setHours] = useState(filter.max_age_hours)
  const [phrases, setPhrases] = useState(filter.exclude_phrases.join(', '))

  // The card is re-rendered from a refetch after every save, and after a pack is
  // added — which merges its exclusions into this very list. Without this the
  // textarea would keep showing what the tenant typed before the merge and the
  // next save would silently undo it.
  useEffect(() => {
    setHours(filter.max_age_hours)
    setPhrases(filter.exclude_phrases.join(', '))
  }, [filter.max_age_hours, filter.exclude_phrases])

  const save = useMutation((value: { hours: number; phrases: string[] }) =>
    call(() =>
      api.PUT('/api/v1/outreach/discovery-settings', {
        body: { max_age_hours: value.hours, exclude_phrases: value.phrases },
      }),
    ),
  )

  async function onSave(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({
      hours,
      phrases: phrases
        .split(',')
        .map((phrase) => phrase.trim())
        .filter(Boolean),
    })
    if (result.ok) {
      toast.show({ kind: 'success', title: 'Saved', body: 'The next search uses these.' })
      onChanged()
    }
  }

  return (
    <form className="ar-card" onSubmit={(event) => void onSave(event)}>
      <div className="ar-card__header">
        <h3>What counts as a lead</h3>
      </div>
      <div className="ar-card__body ar-stack">
        <Field
          label="Only posts newer than"
          help="A post we cannot date is dropped, never kept. Answering a three-month-old request reads worse than not answering it."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              value={hours}
              disabled={!manageable}
              onChange={(event) => {
                setHours(Number(event.target.value))
                save.reset()
              }}
            >
              {windowOptions(filter.max_age_hours).map((option) => (
                <option key={option.hours} value={option.hours}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field
          label="Skip posts containing"
          help={
            filter.customised
              ? 'Your list. Comma separated. Recruitment posts match every keyword and are never a lead.'
              : "AugRank's defaults, shown so you can see what is filtering. Editing them makes the list yours."
          }
        >
          {(aria) => (
            <textarea
              className="ar-textarea"
              {...aria}
              rows={4}
              value={phrases}
              disabled={!manageable}
              onChange={(event) => {
                setPhrases(event.target.value)
                save.reset()
              }}
            />
          )}
        </Field>
        {save.state.failure ? <InlineError failure={save.state.failure} /> : null}
      </div>
      {manageable ? (
        <div className="ar-card__footer">
          <Button type="submit" tone="primary" size="sm" busy={save.pending}>
            Save
          </Button>
          <span className="ar-card__spacer" />
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            Posts whose age we cannot read are always dropped.
          </span>
        </div>
      ) : null}
    </form>
  )
}

function PacksCard({
  packs,
  manageable,
  onChanged,
}: {
  packs: Pack[]
  manageable: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const add = useMutation((slug: string) =>
    call(() => api.POST('/api/v1/outreach/packs/{slug}', { params: { path: { slug } } })),
  )
  const remove = useMutation((slug: string) =>
    call(() => api.DELETE('/api/v1/outreach/packs/{slug}', { params: { path: { slug } } })),
  )

  async function onAdd(pack: Pack): Promise<void> {
    const result = await add.run(pack.slug)
    if (result.ok) {
      const title =
        result.data.keywords_added === 0
          ? `${pack.name} is up to date.`
          : `Added ${String(result.data.keywords_added)} keywords from ${pack.name}.`
      toast.show(
        result.data.exclusions_added > 0
          ? {
              kind: 'success',
              title,
              body: `${String(result.data.exclusions_added)} phrases were added to your skip list.`,
            }
          : { kind: 'success', title },
      )
      onChanged()
    }
  }

  async function onRemove(pack: Pack): Promise<void> {
    const result = await remove.run(pack.slug)
    if (result.ok) {
      toast.show({
        kind: 'info',
        title: `Stopped searching ${pack.name}.`,
        body: 'Its keywords are switched off, not deleted — your leads from them are kept.',
      })
      onChanged()
    }
  }

  const busy = add.pending || remove.pending

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Niche keyword packs</h3>
        <span className="ar-card__spacer" />
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          Curated starting points
        </span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {packs.map((pack) => {
          const outdated =
            pack.active && pack.installed_version !== null && pack.installed_version < pack.version
          return (
            <div className="app-conn" key={pack.slug}>
              <span className="app-conn__logo" aria-hidden="true">
                {pack.icon}
              </span>
              <span className="app-conn__body">
                <strong>{pack.name}</strong>
                <span>
                  {pack.vertical} · {pack.keyword_count} keywords · {pack.exclusion_count}{' '}
                  exclusions
                </span>
                <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
                  {pack.summary}
                </span>
              </span>
              {pack.active && !outdated ? (
                <span className="ar-badge ar-badge--success">✓ Active</span>
              ) : null}
              {outdated ? (
                <span className="ar-badge ar-badge--warning">
                  v{pack.version} adds {pack.new_terms}
                </span>
              ) : null}
              {manageable ? (
                <Button
                  tone={pack.active ? 'quiet' : 'ghost'}
                  size="sm"
                  busy={busy}
                  onClick={() => void (pack.active && !outdated ? onRemove(pack) : onAdd(pack))}
                >
                  {outdated ? 'Update' : pack.active ? 'Remove' : 'Add'}
                </Button>
              ) : null}
            </div>
          )
        })}
        {add.state.failure ? <InlineError failure={add.state.failure} /> : null}
        {remove.state.failure ? <InlineError failure={remove.state.failure} /> : null}
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          Adding a pack also adds its exclusions to your skip list, where you can edit them.
        </span>
      </div>
    </div>
  )
}

function LastRunCard({ run }: { run: LastRun }): JSX.Element {
  if (run.day === null || run.first_filed_at === null) {
    return (
      <div className="ar-card">
        <div className="ar-card__header">
          <h3>Latest run</h3>
        </div>
        <div className="ar-card__body">
          <Empty
            title="Nothing searched yet"
            body="Once your paired browser runs a search, this shows what each filter dropped and how many leads survived."
          />
        </div>
      </div>
    )
  }

  const rows: { label: string; value: number; note: string; strong?: boolean }[] = [
    {
      label: 'Posts read',
      value: run.posts_read,
      note: `${String(run.keywords_searched)} ${run.keywords_searched === 1 ? 'search' : 'searches'}`,
    },
    {
      label: 'Skipped — no address in the post',
      value: run.no_address,
      note: 'nothing to write to',
    },
    { label: 'Skipped — excluded phrase', value: run.excluded_phrase, note: 'mostly hiring posts' },
    { label: 'Skipped — too old', value: run.too_old, note: 'outside your window' },
    {
      label: 'Skipped — found under another keyword',
      value: run.duplicate,
      note: 'counted once',
    },
    {
      label: 'Skipped — already contacted',
      value: run.already_contacted,
      note: 'never re-contacted',
    },
    { label: 'New leads', value: run.new_leads, note: 'carried an address', strong: true },
  ]

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Latest run — {formatDateIn(run.first_filed_at, IST)}</h3>
        <span className="ar-card__spacer" />
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          from {formatTime(run.first_filed_at, IST)} IST
        </span>
      </div>
      <div className="ar-card__body">
        <div className="ar-table-wrap">
          <table className="ar-table" style={{ background: 'transparent' }}>
            <caption className="ar-visually-hidden">
              What the latest day of discovery read and dropped
            </caption>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.strong ? <strong>{row.label}</strong> : row.label}</td>
                  <td className="ar-num">
                    {row.strong ? <strong>{row.value}</strong> : row.value}
                  </td>
                  <td className="ar-num ar-subtle">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
